import { PhoneProvider } from '@compass/client/types/phone_number';
import { phoneNumberService } from '@services/api';
import { useQuery } from '@tanstack/react-query';

/**
 * A program's registered outgoing numbers, grouped by the carrier that owns them.
 *
 * The registry is the only source: it is the one place a number's carrier is
 * recorded, and a call has to present a caller ID the carrier actually owns or the
 * dial is rejected. Grouping here is what makes the dialpad's carrier control real
 * rather than cosmetic — the groups *are* the carriers on offer, so a program with
 * numbers for one carrier gets no control and a third carrier needs no UI change.
 *
 * **Scoped to the program, not the organisation.** Reading organisation-wide would
 * let a call placed in one program dial out as a number registered for another.
 */
export function useProgramPhoneNumbers(
  organizationId: string | null | undefined,
  programId: string | null | undefined,
) {
  const query = useQuery({
    // Keyed by program as well as organisation: switching program must re-ask
    // rather than reuse another program's answer.
    queryKey: ['programPhoneNumbers', organizationId, programId],
    // Without a program there is no correct answer.
    enabled: Boolean(organizationId && programId),
    // The registry changes rarely and this is read on every dialpad open.
    staleTime: 5 * 60 * 1000,
    queryFn: () =>
      phoneNumberService.listProgramPhoneNumbers({
        organization_id: organizationId ?? '',
        program_id: programId ?? '',
        limit: 100,
      }),
  });

  const byProvider = new Map<PhoneProvider, string[]>();
  for (const row of query.data?.phone_numbers ?? []) {
    // A row missing either half cannot place a call, and an unusable carrier tab in
    // front of an agent is worse than an absent one. PHONE_PROVIDER_UNSPECIFIED is
    // enum value 0, so this falsy check also drops it — a separate `=== UNSPECIFIED`
    // comparison here is dead code TypeScript itself rejects (no overlap once
    // narrowed by this guard).
    if (!row.phone || !row.provider) continue;
    const numbers = byProvider.get(row.provider) ?? [];
    numbers.push(row.phone);
    byProvider.set(row.provider, numbers);
  }

  const order = carrierOrder(query.data?.provider_order, [...byProvider.keys()]);
  const providers = order.filter(provider => byProvider.has(provider));

  return { byProvider, providers, order, isLoading: query.isLoading };
}

/**
 * The global carrier order ops set in dynconfig (BV-2452): its first carrier is
 * "Provider 1" and the default pick.
 *
 * An offered carrier the server leaves out goes last, in enum order, so every offered
 * carrier has a label. An older server sends no order, which leaves enum order: the
 * labels the dialpad had before.
 */
function carrierOrder(
  configured: PhoneProvider[] | undefined,
  offered: PhoneProvider[],
): PhoneProvider[] {
  const order = [...new Set(configured ?? [])];
  for (const provider of [...offered].sort((a, b) => a - b)) {
    if (!order.includes(provider)) order.push(provider);
  }
  return order;
}
